import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { GlobalService } from './global.service';
import { map, catchError, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment';
import { Observable, BehaviorSubject, throwError, Subject, of } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { CreditActivityService } from './credit-activity.service';
import * as CryptoJS from 'crypto-js';


const devUrl = environment.backendurl;

/**
 * Data structure of each request object for person-search insights export
 */
export interface ExportRequest {
  type: string
  action?: string
  data: {
    currentTab: string
  }
}

/**
 * Data structure of each service payload object for person-search insights export
 */
export interface ExportPayload {
  type?: string
  tab?: 'overview'
  totalresultcount?: number
  data?: any
  aiData?: string
  aiRequestFilters?: any
  aiRequestPayload?: any[]
  [key: string]: any
}

export interface PersonSearchListing {
  name: string;
  count: number;
  meta: any;
}

export interface PersonSearchRecommendCandidatesRequest {
  startindex: number;
  resultsize: number;
  searchviaboolean: boolean;
  booleanquery: string;
  currentjobtitle: boolean;
  currentcompaniesonly: boolean;
  pastcompaniesonly: boolean;
  jobtitles: string[];
  preferredskills: string[];
  mandatoryskills: string[];
  locations: string[];
  companies: string[];
  industries: string[];
  exclusionlist: string[];
  genders: string[];
  schools: string[];
  schooltypes: string[];
  educationaldegrees: string[];
  educationalmajors: string[];
  candidateshavingemails: boolean;
  candidateshavingcontactnumbers: boolean;
  minexperience: number;
  maxexperience: number;
  minexperienceinccurrentcompany: number;
}

export interface PersonSearchRecommendCandidatesResponse {
  status: string;
  data: {
    totalresultscount: number;
    results?: RecommendCandidateResult[];
    elasticsearchquery?: {}
  }
}

export interface RecommendCandidateResult {
  persondetails: PersonDetails;
  matchscore: number;
}

export interface PersonDetails {
  id: string;
  full_name: string;
  first_name: string;
}

export interface Similarobject {
  jobtitle: { key: string, isMandatory: boolean }[] | [];
  jobcompanyname: { key: string, isMandatory: boolean }[] | [];
  jobindustryname: { key: string, isMandatory: boolean }[] | [];
  pastindustries: string[];
  schools:  { key: string, isMandatory: boolean }[] | string[];
  currentlocation: { key: string, isMandatory: boolean }[] | [];
  skills: { key: string, isMandatory: boolean }[] | [];
  degrees: { key: string, isMandatory: boolean }[] | [];
  fieldOfStudy?: { key: string, isMandatory: boolean }[] | [];
  elasticid: string | number;
  companysize: { key: string, isMandatory: boolean }[] | [];
  fullname: string;
  totaltenure?: string | number;
  linkedinurl: string;
  isUploadedFromResume: boolean;
  locationregion: string;
  locationcontinent: string;
  fromYrsOfExperience?: string | number;
  toYrsOfExperinece?: string | number;
  firstname?: string;
  lastname?: string;
}

export interface shareSavedSearch{
  grant: number[];
  revoke: number[];
  savedsearches: number[];
}

@Injectable({
  providedIn: 'root'
})
export class PersonSearchService {
  public recommendcandidates: any
  Similarobject: any;
  private companyDetailSubject = new BehaviorSubject<any>({});
  private talentinsightssubject = new BehaviorSubject<any>({});
  private companyinsightsfilterai = new BehaviorSubject<any>({});
  private talentinsightsfilterai= new BehaviorSubject<any>({})
  private insightsdata= new BehaviorSubject<any>({})
  private geoIpLocationOfUser = new BehaviorSubject<any>(this.locaStorageService.getItem('userGeoIpLocation') ? JSON.parse(this.locaStorageService.getItem('userGeoIpLocation')) : {});
  private revealcontactstatus = new Subject<any>();

  /**
   * Observable for handling insights-export request signal
   */
  private insightsExportRequestObservable: Subject<any> = new Subject();
  /**
   * Observable for handling insights-export data sharing
   */
  private insightsExportDataObservable: Subject<any> = new Subject();

  /**
   * Cache of reccomended candidates api
   */
  private reccomendedCandidatesCache: { [key: string]: Observable<any> } = {}


  personSearchRequestCache: { [key: string]: Observable<any> } = {};

  companyDetailNext(data: any) {
    this.companyDetailSubject.next(data);
  }

  get companyDetail(): Observable<any> {
    return this.companyDetailSubject.asObservable();
  }

  setTalentinsights(data: any) {
    this.talentinsightssubject.next(data);
  }

  get gettalentinsights(): Observable<any> {
    return this.talentinsightssubject.asObservable();
  }

  setCompanyInsightsFilterAI(data: any) {
    this.companyinsightsfilterai.next(data);
  }

  get getcompanyinsightsfilterAI(): Observable<any> {
    return this.companyinsightsfilterai.asObservable();
  }

  setTalentinsightsFilterAI(data: any) {
    this.talentinsightsfilterai.next(data);
  }

  get getTalentinsightsFilterAI(): Observable<any> {
    return this.talentinsightsfilterai.asObservable();
  }

  setquicksearchinsightsdata(data: any) {
    this.insightsdata.next(data);
  }

  get getquicksearchinsightsdata(): Observable<any> {
    return this.insightsdata.asObservable();
  }

  get getGeoIpLocationOfUser(): Observable<any> {
    return this.geoIpLocationOfUser.asObservable();
  }

  setGeoIpLocationOfUser(data: any) {
    this.geoIpLocationOfUser.next(data);
  }

  constructor(private http: HttpClient, private locaStorageService: LocalStorageService, private creditActivityService: CreditActivityService) { }

  /**
   * Getter for insights-export request observable
   */
  get insightsExportRequestEvent(): Observable<any> {
    return this.insightsExportRequestObservable.asObservable();
  }

  /**
   * Getter for insights-export request observable
   */
  get insightsExportDataEvent(): Observable<any> {
    return this.insightsExportDataObservable.asObservable();
  }

  /**
   * Send request for insights export
   * @param requestData
   */
  sendInsightsExportRequest(requestData: ExportRequest) {
    this.insightsExportRequestObservable.next(requestData);
  }

  /**
   * Emit data for insights export
   * @param exportData
   */
  sendInsightsExportData(exportData: ExportPayload) {
    this.insightsExportDataObservable.next(exportData);
  }

  /**
   * Get Person Search Listing
   * @param keyword
   * @returns PersonSearchListing[]
   */
  getPersonSearchListing(keyword: string,type: string = null,continent?: string ): Observable<PersonSearchListing[]> {
    let url = devUrl + "/personsearch/typeaheads"
    const queryParams = { keyword: keyword };
    type && (queryParams['type'] = type);
    if(continent) queryParams['continent'] = continent;
    return this.http.get<PersonSearchListing[]>(url, { params: queryParams }).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('Get Person Search Listing', url)))
  }


  getPersonSearchRecommendCandidates(requestObject: any, cacheRequired: boolean = false): Observable<any> {
    const hash = CryptoJS.MD5(JSON.stringify(requestObject)).toString();
    if(cacheRequired && Reflect.has(this.reccomendedCandidatesCache, hash)){
        return of(this.reccomendedCandidatesCache[hash])
    }
    let url = devUrl + "/personsearch/recommendcandidates";
    return this.http.post<any>(url, requestObject).pipe(map(response => { return response; }), tap(data => {
      this.reccomendedCandidatesCache[hash] = data
    })
      , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getCandidateInsights(): Observable<any> {
    const url = devUrl + '/personsearch/allcandidatesinsights';
    return this.http.get<any>(url).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('Get Person Search Listing', url)));

  }

  getTalentPoolBucketList(): Observable<any> {
    const url = devUrl + '/personsearch/talentpoolbucketslist';
    return this.http.get<any>(url).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getRefreshedCandidate(candidateObject): Observable<any> {
    const url = devUrl + '/personsearch/refreshcandidatedata';
    return this.http.put<any>(url, candidateObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getTotalAggs(requestobject, cacheRequired: boolean = false):  Observable<any> {
    const url = devUrl + '/personsearch/aggregates';
    const hash = CryptoJS.MD5(JSON.stringify(requestobject)).toString();
    if(Reflect.has(this.personSearchRequestCache, hash) && cacheRequired){
      // console.log('cache hit');
      return of(this.personSearchRequestCache[hash])
    }
    return this.http.post<any>(url, requestobject).pipe(map(response => { return response; }), tap(d => {
      this.personSearchRequestCache[hash] = d
    })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  /**
   * Used to get search history
   * @param startindex
   * @param module history of each module e.g personsearch, companyinsights, talentinsights
   * @returns
   */
  getSearchHistory(startindex, module = 'personsearch'): Observable<any> {
    const url = devUrl + `/personsearch/searchhistory?startindex=${startindex}&module=${module}`;
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  /**
   * Used to get saved search history
   * @param startindex
   * @param module  history of each module e.g personsearch, companyinsights, talentinsights
   * @param talentdiscoveryhistoryid
   * @returns
   */
  getSaveSearchHistory(startindex, module = 'personsearch', talentdiscoveryhistoryid: string = null): Observable<any> {
    const url = devUrl + `/personsearch/savedsearchhistory?startindex=${startindex}&module=${module}`;
    return this.http.get<any>(url, { params: talentdiscoveryhistoryid ? { talentdiscoveryhistoryid } : {} }).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  checkSearchNameDuplicacy(searchname: string, module = 'personsearch'): Observable<any> {
    const url = devUrl + `/personsearch/search/name/duplicate?name=${searchname.trim()}&module=${module}`;
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Check save search name duplicacy', url)));
  }

  saveSearch(payload: any, update: boolean = false): Observable<any> {
    let url = devUrl + `/personsearch/search/save`;
    let queryParams = new HttpParams();
    if(update) {
      queryParams = queryParams.append('update', 'true');
      url = url + '?' + queryParams.toString();
    }

    return this.http.put(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Save search', url)));
  }

  getdetailsfromlinkedin(linkedinurl:any): Observable<any> {
    const url = devUrl + `/personsearch/detailsfromlinkedinurl?linkedinurl=${linkedinurl}`;
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }
  previewContactDetails(elasticid): Observable<any> {
    const url = devUrl + `/personsearch/previewcontactdetails?elasticid=${elasticid}`
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  revealContact(data) : Observable<any> {
    const url = devUrl + '/personsearch/revealcontact';
    return this.http.put<any>(url, data).pipe(
      map(response => { return response; }),
      // Notify on every response, not just status === 'success' - a 200 response with
      // status !== 'success' (e.g. "not enough credits") still needs a refresh so the UI shows
      // the real current balance, and catchError below covers actual HTTP-level failures.
      tap((res: any) => { this.creditActivityService.notifyCreditConsumed(); }),
      catchError((err: any) => {
        this.creditActivityService.notifyCreditConsumed();
        return GlobalService.handleError('Get Person Search Listing', url)(err);
      }));
  }

  addToTanentpool(data):  Observable<any> {
    const url = devUrl + '/personsearch/addtotalentpool';
    return this.http.post<any>(url, data).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  markContactValidity(payload): Observable<any> {
    const url = devUrl + '/candidate/contact/validate';
    return this.http.put<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Mark contact validity', url)));
  }

  getrecordsCount(): Observable<any> {
    const url = devUrl + `/personsearch/recordscount`
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person record count', url)));
  }




  public passsimilarcandidatesdata (data): void {
    this.recommendcandidates = data;
  }

  public getsimilarcandidatesdata (){
    return this.recommendcandidates;
  }

  parseresume(data) {
    let formData: FormData = new FormData();
    formData.append('resume', data);
    var url = devUrl + "/personsearch/parseresume"
    return this.http.post(url, formData).pipe(
      map(response => { return response; })
      , catchError(GlobalService.handleError('Resume Parsing', url)));
  }

  getRecentAttritionData(requestObject): Observable<any> {
    const url = devUrl + '/personsearch/recentcompanyattrition';
    return this.http.post(url, requestObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  /**
   * @deprecated
   * @param requestObject
   * @returns
   */
  getKeyPeople(requestObject): Observable<any> {
    const url = devUrl + '/personsearch/detailsfrompdlids';
    return this.http.post<any>(url, { ...requestObject }).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getRecentHires(requestObject): Observable<any> {
    const url = devUrl + '/personsearch/recentcompanyhires';
    return this.http.post(url, requestObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getBooleanQuery(data): Observable<any> {
    const url = devUrl + '/personsearch/booleanquery';
    return this.http.post<any>(url, data ).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Boolean Query', url)));
  }

  saveProfileView(data):  Observable<any> {
    const url = devUrl + '/personsearch/saveprofileview';
    return this.http.put<any>(url, data).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Boolean Query', url)));
  }


  /**
   * @deprecated
   * Use `getCompanyLogoV2` to get company logos
   * @param data
   * @returns
   */
  getCompanyLogo(data): Observable<any> {
    const url = devUrl + '/personsearch/companylogo';
    return this.http.post<any>(url, data).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  /**
 * Get company logo on the basis of id and name,
   * Payload contains array of object
   * Each object having key `id` and value `companyid`
   * If company id not present than object contains key `name` and value `companyname`
   * @param data
   *  payload: [
    *  {
    *     id: 'hWBI7x4FvSurVNWDXD4uFgQt5ges',
    *     name: 'amazon'
    *  },
    *  {
    *     id: 'hWBI7x4FvSurVNWDXD4uFgQt5ges'
    *  },
    *  {
    *    name: 'google'
    *  }
    * ]
   * @returns
   */
  getCompanyLogoV2(data): Observable<any> {
    const url = devUrl + `/personsearch/v2/companylogo`;
    return this.http.post<any>(url, data, { observe: 'response' }).pipe(
      map(response => ({ ...response.body, statusCode: response.status })),
      catchError(err => throwError(err))
    );
  }

  /**
   * Export candidate profile in PDF
   * @param payload
   */
  exportCandidateProfileToPDF(payload: Record<string, any>): Observable<any> {
    const url = devUrl + '/personsearch/candidate/export';
    return this.http.post(url, payload, { responseType: 'blob' }).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }


  makeRequestContact(requestObject: any): Observable<any> {
    const url = devUrl + "/personsearch/candidateintroduction";
    return this.http.post<any>(url, requestObject).pipe(
      map(response => { return response; }),
      // Notify on every response, not just status === 'success' - a 200 response with
      // status !== 'success' (e.g. "not enough credits") still needs a refresh so the UI shows
      // the real current balance, and catchError below covers actual HTTP-level failures.
      tap((res: any) => { this.creditActivityService.notifyCreditConsumed(); }),
      catchError((err: any) => {
        this.creditActivityService.notifyCreditConsumed();
        return GlobalService.handleError('Error while making contract request', url)(err);
      }));
  }

  getCandidateActivity(elastic_id): Observable<any> {
    const url = devUrl + `/personsearch/candidateactivity?elastic_id=${elastic_id}`;
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person record count', url)));
  }


  getLastCandidateActivity(elastic_id): Observable<any> {
    const url = devUrl + `/personsearch/lastcandidateactivity?elastic_id=${elastic_id}`;
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person record count', url)));
  }

  getCandidateActivityInBulk(elastic_ids): Observable<any> {
    const url = devUrl + `/personsearch/bulkcandidateactivity?elastic_id=${elastic_ids}`;
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person record count', url)));
  }
  gettalentinsightssummary(requestObject): Observable<any> {
    const url = devUrl + '/openai/talentinsightssummary';
    return this.http.post(url, requestObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getquicksearchsummary(requestObject): Observable<any> {
    const url = devUrl + '/openai/quicksearchsummary';
    return this.http.post(url, requestObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  /**
   * Company Attrition data,
   * @param data
   * @returns
   */
  getCompanyAttrition(data): Observable<any> {
    const url = devUrl + '/company/attrition';
    return this.http.post(url, data).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  /**
   * Company Growth data
   * @param data
   * @returns
   */
  getCompanyGrowth(data): Observable<any>{
    const url = devUrl + '/company/growth';
    return this.http.post(url, data).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  /**
   * Used to store history of talent-insights and company-insights
   * @param data
   * @returns
   */
  setInsightsHistory(data): Observable<any> {
    const url = devUrl + '/personsearch/history';
    return this.http.put<any>(url, data, { observe: 'response' }).pipe(map((response) => ({ ...response.body, statusCode: response }),
      catchError((err: HttpErrorResponse) => throwError(err))));
  }


  //Revamp services of Single Reveal flow

  /**
   * To Reveal Contact details revamp version
   * @param data
   * @returns
   */
  revealContactV2(data: { elastic_id: string, enrichedfrom: string}): Observable<any> {
    const url = devUrl + '/personsearch/v2/revealcontact';
    return this.http.put<any>(url, data, { observe: 'response' }).pipe(
      map(response => ({ ...response, statusCode: response.status })),
      // reaching here means a 2xx response - consumes contact credits, let TransactionService know
      tap(() => { this.creditActivityService.notifyCreditConsumed(); }),
      catchError((err: HttpErrorResponse) => {
        // also refresh on failure - the failure itself may be caused by credits having changed
        // (e.g. a superadmin adjustment mid-session), so the UI should reflect the current balance
        this.creditActivityService.notifyCreditConsumed();
        return throwError(err);
      }));
  }

  /**
   * Retry reveal contact
   * @param data
   * @returns
   */
  revealRetry(data: { enrichedFrom: string, elasticId: string }): Observable<any> {
    const url = devUrl + `/personsearch/revealcontact/retry`;
    return this.http.put<any>(url, data, { observe: 'response' }).pipe(
      map(response => ({ ...response, statusCode: response.status })),
      // reaching here means a 2xx response - consumes contact credits, let TransactionService know
      tap(() => { this.creditActivityService.notifyCreditConsumed(); }),
      catchError((err: HttpErrorResponse) => {
        // also refresh on failure - the failure itself may be caused by credits having changed
        // (e.g. a superadmin adjustment mid-session), so the UI should reflect the current balance
        this.creditActivityService.notifyCreditConsumed();
        return throwError(err);
      }));
  }

  /**
   * Preview of contact details for revampversion
   * @param elasticid
   * @returns
   */
  previewContactDetailsV2(elasticid: string): Observable<any> {
    const url = devUrl + `/personsearch/v2/previewcontactdetails?elasticid=${elasticid}`
    return this.http.get<any>(url).pipe(map(response => { return response; })
    , catchError((err: HttpErrorResponse) => throwError(err)));
  }

  /**
   * Get person-search tours
   * @param optionalParams optional query-params
   */
  getPersonSearchTours(optionalParams: { module?: string, all?: string } = {}) {
    const url = devUrl + `/personsearch/tours`
    return this.http.get<any>(url, { params: optionalParams }).pipe(map(response => { return response; })
    , catchError((err: HttpErrorResponse) => throwError(err)));
  }

  /**
 * Used to get values of highlight filter in person search filters
 * @returns response
 */
  getHighlightsOptions() {
    const url = devUrl + `/personsearch/profilehighlighttags`
    return this.http.get<any>(url).pipe(map(response => { return response; })
      , catchError((err: HttpErrorResponse) => throwError(err)));
  }

  /**
   * Used to update preference of user in localstorage
   * @param data
   * @returns
   */
  updatePagePreference(data: { preference_type: string, listing_type: string, preference_value: string | number }) {
    try {
      const userdata = this.locaStorageService.getItem('userdata');
      if (!userdata) throw 'userData not found';
      const preferenceArray = (userdata.preferenceData) ? userdata.preferenceData : [];
      preferenceArray.length && preferenceArray.map(item => {
        if (item.listing_type == data.listing_type && item.preference_type == data.preference_type) {
          item.preference_value = data.preference_value;
        }
      })
      userdata.preferenceData = preferenceArray;
      this.locaStorageService.setItem('userdata', userdata);
      return true
    } catch (err) {
      throw err;
    }
  }

    /**
   * Get shared users
   * @param payload
   */
     getSharedSavedSearches(payload: { savedsearches: (string | number)[] }, onlyActive = true) {
      let url = devUrl + '/personsearch/savedsearch/shared/users';
      return this.http.post(url, payload, { params: !onlyActive ? { all: 'true' } : {} }).pipe(map(response => { return response; })
        , catchError(GlobalService.handleError('Get shared saved searches', url)));
    }

      /**
   * @param payload
   */
  shareSavedSearch(payload: shareSavedSearch): Observable<shareSavedSearch> {
    const url = devUrl + "/personsearch/savedsearch/share ";
    return this.http.post<shareSavedSearch>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Share Saved Search', url)));
  }

  openEuDoc() {
    var url = devUrl + "/personsearch/eudataaccesssopdocument"
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((response:any) => {return response})

          , catchError(GlobalService.handleError('resumeurl', url)));
  }

  /**
   * Export insights report to PDF
   * @param payload
   */
  exportInsightsToPDFReport(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/insights/export';
    return this.http.post(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Company Insights Export to PDF', url)));
  }

  candidateCompare(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/candidate-compare';
    return this.http.post<any>(url, payload).pipe(map(response =>  ({ ...response, statusCode: response.status }))
    , catchError(GlobalService.handleError('Candidate Compare', url)));
  }

  getJdSearchActivity(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/jdsearchactivity';
    return this.http.post(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('JD Search Activity', url)));
  }

  getJdInsights(payload: any): Observable<any> {
        const url = devUrl + '/personsearch/jd-insights';
        return this.http.post(url, payload).pipe(map(response => { return response; })
        , catchError(GlobalService.handleError('JD Insights', url)));
  }

  getNLSearchData(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/nl-search';
    return this.http.post(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('NL Search', url)));
  }

  getRecommendCount(payload:any): Observable<any> {
      const url = devUrl + '/personsearch/count';
    return this.http.post(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Person search count', url)));
  }

  getPreferredContinents() {
      var url = devUrl + "/user/preferredsearchcontinents"
  return this.http.get<any>(url).pipe(map(response => { return response; })
      , catchError((err: HttpErrorResponse) => throwError(err)));
  }




  setRevealContactStatusEvent(data: any) {
    this.revealcontactstatus.next(data);
  }

  get getRevealContactStatusEvent(): Observable<any> {
    return this.revealcontactstatus.asObservable();
  }
}
